import { useMemo } from 'react'
import { useState } from 'react'
import { useAdminArticles } from '../api/hooks/useArticles'
import { usePages } from '../api/hooks/usePages'
import { useWireAuth } from '../auth/useWireAuth'
import type { WireWorkflowStage } from '../api/types'
import { Plus, RotateCcw } from 'lucide-react'
import { isActionNeeded } from '../components/dashboard/ActionNeededList'
import { AdminWidgetBoard } from '../components/dashboard/AdminWidgetBoard'

function WLogo({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 125.409 98.041" className={className} aria-hidden>
      <g transform="translate(0 98.887)">
        <path d="M0,0,48.06,98.041,64.4,69.206,30.278,0Z" transform="translate(0 -98.887)" fill="currentColor" />
        <path d="M0,0,48.059,98.041,64.4,69.206,30.277,0Z" transform="translate(40.35 -98.887)" fill="currentColor" />
        <path d="M0,0,31.149,63l13.5-23.818L25.672,0Z" transform="translate(80.762 -98.885)" fill="currentColor" />
      </g>
    </svg>
  )
}

/** Stages that represent active in-progress work */
const IN_PROGRESS_STAGES = new Set<WireWorkflowStage>([
  'qc_review', 'layout_review', 'editorial_approval', 'scheduled', 'post_pub_review',
])

export default function AdminDashboardPage() {
  const { user } = useWireAuth()
  const userSub = user?.username ?? user?.sub ?? ''
  const [pickerOpen, setPickerOpen] = useState(false)

  // All in-progress articles (pipeline total + blocked gauge)
  // We use the same client-side pattern as ArticleListPage — the backend
  // assignedToMe param is not reliably supported, so we filter locally.
  const { data: allArticles = [], isPending: allLoading } = useAdminArticles()
  const inProgress = useMemo(
    () => allArticles.filter(a => a.workflow && (
      IN_PROGRESS_STAGES.has(a.workflow.stage) || Boolean(a.workflow.activeBlock)
    )),
    [allArticles]
  )

  // Articles in-progress that are assigned to me in any role
  const myInProgress = useMemo(
    () => inProgress.filter(a => [
      a.workflow?.qcAssignedTo,
      a.workflow?.layoutAssignedTo,
      a.workflow?.editorialAssignedTo,
      a.workflow?.postPubAssignedTo,
    ].includes(userSub)),
    [inProgress, userSub]
  )

  const actionNeededCount = useMemo(
    () => myInProgress.filter(a => isActionNeeded(a, userSub)).length,
    [myInProgress, userSub]
  )

  // Front pages
  const { data: livePages = [], isPending: liveLoading } = usePages({ kind: 'front-page', status: 'live' })
  const { data: scheduledPages = [], isPending: scheduledLoading } = usePages({ kind: 'front-page', status: 'scheduled' })

  const livePage = livePages[0]
  const nextScheduled = scheduledPages.length > 0
    ? scheduledPages.slice().sort((a, b) =>
        new Date(a.scheduledFor ?? 0).getTime() - new Date(b.scheduledFor ?? 0).getTime()
      )[0]
    : undefined

  const loading = allLoading

  return (
    <div className="min-h-screen bg-canvas">

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex items-stretch justify-between border-b border-line bg-surface px-8 py-5">
        <div>
          <p className="font-sans text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
          <h1 className="mt-1 font-zodiak text-2xl font-bold text-fg">
            Good{getTimeOfDay()},{' '}
            <span className="text-accent-primary-fg">
              {user?.name?.split(' ')[0] ?? 'Editor'}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2.5 select-none">
          <button
            type="button"
            onClick={() => {
              const event = new CustomEvent('wire-ui-admin-reset-layout')
              window.dispatchEvent(event)
            }}
            className="inline-flex items-center gap-1.5 rounded border border-line bg-surface px-3 py-2 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          >
            <RotateCcw size={12} aria-hidden="true" />
            Reset Layout
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen(current => !current)}
            className="inline-flex items-center gap-1.5 rounded border border-line bg-surface px-3 py-2 text-[11px] font-semibold text-fg transition-colors hover:bg-fill"
          >
            <Plus size={12} aria-hidden="true" />
            Add Widgets
          </button>
          <WLogo className="h-7 w-auto text-accent-primary-fg" />
          <span className="font-zodiak text-2xl font-light text-fg-secondary">·</span>
          <p className="font-zodiak text-2xl font-light text-fg-secondary">WIRed In</p>
        </div>
      </div>

      <AdminWidgetBoard
        articles={inProgress}
        myArticles={myInProgress}
        actionNeededCount={actionNeededCount}
        loading={loading}
        livePage={livePage}
        scheduledPage={nextScheduled}
        liveLoading={liveLoading}
        scheduledLoading={scheduledLoading}
        userSub={userSub}
        pickerOpen={pickerOpen}
        onPickerOpenChange={setPickerOpen}
      />

    </div>
  )
}

function getTimeOfDay(): string {
  const h = new Date().getHours()
  if (h < 12) return ' morning'
  if (h < 17) return ' afternoon'
  return ' evening'
}
